import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Check,
  Cloud,
  Copy,
  FolderOpen,
  Loader,
  Lock,
  Pencil,
  Plus,
  Save,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";
import { Sheet } from "./ui/Sheet";
import { Button } from "./ui/Button";
import { isUnstartedRoom, useDesign } from "../lib/store";
import { exitCollabOnboarding, getCollabOnboarding } from "../lib/collabOnboarding";
import { useLibrary, type CloudStatus } from "../lib/library";
import { useAuth } from "../lib/auth";
import { useHasUnsavedChanges } from "../lib/unsavedChanges";
import { boundingBox, formatArea, polygonArea } from "../lib/geom";
import { resolveFurniture } from "../lib/furniture/resolve";
import { buildRenderPlan } from "../lib/furniture/renderPlan";
import { toSvgPath } from "../lib/furniture/path";
import { mountOnWall, openingOnWall, slidingPanels } from "../lib/floorPlanMarkers";
import { interiorSegments } from "../lib/interiorWalls";
import { formatRelative } from "../lib/formatTime";
import type { Design, SavedRoom } from "../lib/types";
import { cn } from "../lib/cn";
import { SignInPanel } from "./SignInPanel";
import { subscribeSharedInbox } from "../lib/sharedRoomsCloud";
import type { SharedInboxEntry } from "../lib/sharedRoomTypes";
import { exitSharedRoom, openRoom } from "../lib/openRoom";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function RoomsSheet({ open, onClose }: Props) {
  return (
    <Sheet open={open} onClose={onClose} title="My rooms" side="right">
      {/* Body is a child component so it remounts on each open — keeps
          ephemeral state (flash indicators, etc.) cleanly scoped to a
          single session of the sheet being visible. */}
      <RoomsSheetBody onClose={onClose} />
    </Sheet>
  );
}

function RoomsSheetBody({ onClose }: { onClose: () => void }) {
  const auth = useAuth();

  // Saving and browsing rooms are gated behind sign-in. Branch early so
  // we don't render the action/list scaffolding for users who can't use
  // it — the SignedOutPrompt explains the policy and offers sign-in.
  if (auth.status === "loading") {
    return <LoadingPlaceholder />;
  }
  if (auth.status !== "signed-in") {
    return <SignedOutPrompt onClose={onClose} />;
  }

  return <SignedInRoomsBody onClose={onClose} signedIn />;
}

function LoadingPlaceholder() {
  return (
    <div
      role="status"
      aria-label="Loading your account"
      className="flex flex-col gap-3 pt-2"
    >
      <div className="h-14 rounded-xl bg-ink-100/70 animate-pulse" />
      <div className="h-10 rounded-xl bg-ink-100/70 animate-pulse" />
      <div className="h-10 rounded-xl bg-ink-100/70 animate-pulse" />
    </div>
  );
}

function SignedOutPrompt({ onClose }: { onClose: () => void }) {
  const roomStarted = useDesign((s) => !isUnstartedRoom(s.design.room));

  function startBlankRoom() {
    if (roomStarted && !confirm("Start a blank room? The current room will be cleared.")) return;
    startBlankDesign();
    onClose();
  }

  return (
    <div className="flex flex-col gap-5 pt-1">
      <Button
        variant="secondary"
        size="md"
        className="w-full"
        onClick={startBlankRoom}
        data-testid="rooms-new-blank-room"
      >
        <Plus className="size-4" /> New blank room
      </Button>

      <div className="border-t border-ink-100 pt-4">
        <div className="flex items-start gap-3">
          <div className="size-9 shrink-0 rounded-xl bg-ink-50 text-ink-700 ring-1 ring-ink-200 flex items-center justify-center">
            <Lock className="size-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-ink-900">
              Sign in to save rooms
            </div>
            <p className="mt-1 text-body-sm leading-snug text-ink-500">
              Back up your work and open it on any device.
            </p>
          </div>
        </div>
        <SignInPanel intro={null} />
      </div>
    </div>
  );
}

/** A fresh, empty room. Inside the collaboration guide this also leaves
 *  the guide, so the starter checklist doesn't narrate a blank canvas. */
function startBlankDesign() {
  const ob = getCollabOnboarding();
  if (ob.active && !ob.dismissed) {
    exitCollabOnboarding("own_room");
    return;
  }
  exitSharedRoom();
  useDesign.getState().resetAll();
}

function SignedInRoomsBody({
  onClose,
}: {
  onClose: () => void;
  // Marker prop so this body only ever instantiates under a signed-in
  // branch — keeps the useLibrary subscription tree out of the
  // signed-out render path.
  signedIn: true;
}) {
  const design = useDesign((s) => s.design);
  const activeSavedRoomId = useDesign((s) => s.activeSavedRoomId);
  const activeSharedRoomId = useDesign((s) => s.activeSharedRoomId);
  const resetAll = useDesign((s) => s.resetAll);

  const rooms = useLibrary((s) => s.rooms);
  const cloudStatus = useLibrary((s) => s.cloudStatus);
  const cloudError = useLibrary((s) => s.cloudError);
  const saveDesign = useLibrary((s) => s.saveDesign);
  const updateDesign = useLibrary((s) => s.updateDesign);
  const renameRoom = useLibrary((s) => s.renameRoom);
  const deleteRoom = useLibrary((s) => s.deleteRoom);

  const activeSaved = useMemo(
    () => rooms.find((r) => r.id === activeSavedRoomId) ?? null,
    [rooms, activeSavedRoomId]
  );

  // Used to gate the "Save" button and the New-room confirmation. See
  // `useHasUnsavedChanges` for the exact definition (shared with the
  // top-bar unsaved-changes indicator).
  const hasUnsavedChanges = useHasUnsavedChanges();

  const [justAction, setJustAction] = useState<"save" | "create" | null>(null);
  const [justSavedId, setJustSavedId] = useState<string | null>(null);

  function flashJust(action: "save" | "create", id: string) {
    setJustAction(action);
    setJustSavedId(id);
    window.setTimeout(() => {
      setJustAction(null);
      setJustSavedId(null);
    }, 1500);
  }

  function handleSave() {
    // A shared room is already persisted by sync, and its id is mutually
    // exclusive with a library link. Forking that design is "New save".
    if (activeSharedRoomId) return;
    if (activeSaved) {
      updateDesign(activeSaved.id, design);
      flashJust("save", activeSaved.id);
      return;
    }
    const fallback = design.room.name || "Untitled room";
    const input = window.prompt("Name this room:", fallback);
    if (input === null) return; // User cancelled
    const name = input.trim() || fallback;
    const id = saveDesign(design, name);
    useDesign.setState({ activeSavedRoomId: id });
    flashJust("save", id);
  }

  function handleNewSave() {
    const fallback = activeSaved
      ? `${activeSaved.name} copy`
      : design.room.name || "Untitled room";
    const input = window.prompt("Name this room:", fallback);
    if (input === null) return; // User cancelled
    const name = input.trim() || fallback;
    const id = saveDesign(design, name);
    useDesign.setState({ activeSavedRoomId: id });
    flashJust("create", id);
  }

  function handleNewRoom() {
    if (hasUnsavedChanges) {
      const message = activeSaved
        ? `Discard unsaved changes to "${activeSaved.name}" and start a new room?`
        : "Discard unsaved changes and start a new room?";
      if (!confirm(message)) return;
    }
    const ob = getCollabOnboarding();
    if (ob.active && !ob.dismissed) exitCollabOnboarding("own_room");
    else resetAll();
    onClose();
  }

  function handleLoad(room: SavedRoom) {
    // Route through `openRoom` so any saved room that's also a shared
    // room (the user is a member of `sharedRooms/{room.id}`) opens
    // straight into multiplayer mode instead of silently degrading to
    // a private snapshot. The fallback design covers the private case.
    void openRoom(room.id, { fallbackDesign: room.design });
    onClose();
  }

  return (
    <div className="flex flex-col gap-5 pt-1">
      <ActionsSection
        activeSaved={activeSaved}
        activeSharedRoomId={activeSharedRoomId}
        hasUnsavedChanges={hasUnsavedChanges}
        justAction={justAction}
        onSave={handleSave}
        onNewSave={handleNewSave}
        onNewRoom={handleNewRoom}
      />

      <CloudStatusBanner status={cloudStatus} error={cloudError} />

      <div className="border-t border-ink-100 pt-4">
        <div className="text-xs font-medium text-ink-500 uppercase tracking-wide mb-3">
          Saved ({rooms.length})
        </div>

        {rooms.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="flex flex-col gap-2">
            {rooms.map((room) => (
              <SavedRoomCard
                key={room.id}
                room={room}
                isActive={room.id === activeSavedRoomId}
                justSaved={room.id === justSavedId}
                onLoad={() => handleLoad(room)}
                onRename={(name) => renameRoom(room.id, name)}
                onDelete={() => {
                  if (
                    confirm(`Delete "${room.name}"? This can't be undone.`)
                  ) {
                    deleteRoom(room.id);
                    if (room.id === activeSavedRoomId) {
                      useDesign.setState({ activeSavedRoomId: null });
                    }
                  }
                }}
              />
            ))}
          </ul>
        )}
      </div>

      <SharedInboxSection onClose={onClose} />
    </div>
  );
}

/**
 * "Shared with me" — lists every multiplayer room the user has been
 * invited to and accepted. Clicking an entry navigates to the shared
 * room route (which, in this single-page app, just sets a URL param
 * the App reads on boot — see `src/App.tsx`).
 */
function SharedInboxSection({ onClose }: { onClose: () => void }) {
  const auth = useAuth();
  const [entries, setEntries] = useState<SharedInboxEntry[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (auth.status !== "signed-in" || !auth.user) {
      setEntries([]);
      setLoaded(true);
      return;
    }
    const unsub = subscribeSharedInbox(
      auth.user.uid,
      (next) => {
        setEntries(next);
        setLoaded(true);
      },
      () => setLoaded(true)
    );
    return () => unsub();
  }, [auth.status, auth.user]);

  if (!loaded) return null;
  if (entries.length === 0) return null;

  function handleOpen(entry: SharedInboxEntry) {
    // Force shared mode for inbox entries — we already know the user is
    // a member (the inbox doc proves it). `openRoom` will mirror the
    // `?shared=` URL as a side effect so the resulting page is
    // deep-linkable.
    void openRoom(entry.roomId, { forceShared: true });
    onClose();
  }

  return (
    <div className="border-t border-ink-100 pt-4">
      <div className="flex items-center gap-2 mb-3">
        <Users className="size-3.5 text-ink-400" />
        <div className="text-xs font-medium text-ink-500 uppercase tracking-wide">
          Shared with me ({entries.length})
        </div>
      </div>
      <ul className="flex flex-col gap-2">
        {entries.map((e) => (
          <li
            key={e.roomId}
            className="flex items-center gap-3 p-2.5 rounded-2xl bg-white ring-1 ring-ink-200 hover:bg-ink-50 transition-colors"
          >
            <div className="size-10 shrink-0 rounded-xl bg-emerald-50 ring-1 ring-emerald-100 flex items-center justify-center">
              <Users className="size-4 text-emerald-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-ink-900 truncate">
                {e.name}
              </div>
              <div className="text-caption text-ink-500 truncate">
                Shared by {e.ownerDisplayName}
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleOpen(e)}
              aria-label={`Open ${e.name}`}
              title="Open"
              className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-ink-700 hover:bg-ink-100 active:bg-ink-200 transition-colors"
            >
              <Upload className="size-4 -rotate-90" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface ActionsSectionProps {
  activeSaved: SavedRoom | null;
  activeSharedRoomId: string | null;
  hasUnsavedChanges: boolean;
  justAction: "save" | "create" | null;
  onSave: () => void;
  onNewSave: () => void;
  onNewRoom: () => void;
}

function ActionsSection({
  activeSaved,
  activeSharedRoomId,
  hasUnsavedChanges,
  justAction,
  onSave,
  onNewSave,
  onNewRoom,
}: ActionsSectionProps) {
  const saveDisabled = activeSharedRoomId
    ? true
    : activeSaved
      ? !hasUnsavedChanges && justAction !== "save"
      : false;
  const saveTitle = activeSharedRoomId
    ? "This shared room saves automatically"
    : activeSaved
      ? hasUnsavedChanges
        ? `Save changes to "${activeSaved.name}"`
        : "No unsaved changes"
      : "Save the current layout to your library";

  return (
    <div className="flex flex-col gap-3">
      {activeSaved && (
        <div className="rounded-xl bg-ink-50 ring-1 ring-ink-100 px-3 py-2 text-caption text-ink-600 flex items-center gap-1.5">
          <FolderOpen className="size-3.5 text-ink-400 shrink-0" />
          <span>Editing</span>
          <span className="font-semibold text-ink-800 truncate min-w-0">
            {activeSaved.name}
          </span>
          {hasUnsavedChanges && (
            <span
              title="You have unsaved changes"
              className="ml-auto inline-flex items-center px-1.5 rounded-full bg-amber-100 text-amber-700 text-label font-semibold uppercase tracking-wide"
            >
              unsaved
            </span>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="primary"
          onClick={onSave}
          disabled={saveDisabled}
          className="flex-1"
          title={saveTitle}
        >
          {justAction === "save" ? (
            <>
              <Check className="size-4" /> Saved
            </>
          ) : (
            <>
              <Save className="size-4" /> Save
            </>
          )}
        </Button>
        <Button
          variant="secondary"
          onClick={onNewSave}
          className="flex-1"
          title="Save the current layout as a new room in your library"
        >
          {justAction === "create" ? (
            <>
              <Check className="size-4" /> Saved
            </>
          ) : (
            <>
              <Copy className="size-4" /> New save
            </>
          )}
        </Button>
        <Button
          variant="secondary"
          onClick={onNewRoom}
          className="flex-1"
          title="Discard the current layout and start a blank room"
        >
          <Plus className="size-4" /> New room
        </Button>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl bg-ink-50 ring-1 ring-ink-100 px-4 py-6 text-center">
      <div className="mx-auto size-10 rounded-full bg-white flex items-center justify-center text-ink-400 ring-1 ring-ink-200">
        <FolderOpen className="size-5" />
      </div>
      <div className="mt-3 text-sm font-medium text-ink-800">
        No saved rooms yet
      </div>
      <div className="mt-1 text-caption text-ink-500">
        Tap “Save” above to save the current layout to your library.
      </div>
    </div>
  );
}

function CloudStatusBanner({
  status,
  error,
}: {
  status: CloudStatus;
  error: string | null;
}) {
  // This banner only renders for signed-in users — the surrounding
  // RoomsSheetBody gates the entire library experience behind sign-in,
  // so we no longer need a signed-out branch here.
  if (status === "syncing") {
    return (
      <div
        role="status"
        className="flex items-center gap-2 rounded-xl bg-ink-50 ring-1 ring-ink-100 px-3 py-2 text-caption text-ink-600"
      >
        <Loader className="size-4 animate-spin text-ink-400" />
        <span>Syncing your rooms…</span>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div
        role="status"
        className="flex items-start gap-2.5 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2.5 text-caption text-amber-800"
      >
        <AlertTriangle className="size-4 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium">Cloud sync paused</div>
          <div className="text-amber-700">
            {error ?? "We'll retry the next time you save a room."}
          </div>
        </div>
      </div>
    );
  }

  // synced / local-only-but-signed-in (which shouldn't really happen,
  // but we render it the same as synced — the rooms are still safe).
  return (
    <div
      role="status"
      className="flex items-center gap-2 rounded-xl bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2 text-caption text-emerald-800"
    >
      <Cloud className="size-4 text-emerald-600" />
      <span>Backed up to your account</span>
    </div>
  );
}

interface CardProps {
  room: SavedRoom;
  isActive: boolean;
  justSaved: boolean;
  onLoad: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
}

function SavedRoomCard({
  room,
  isActive,
  justSaved,
  onLoad,
  onRename,
  onDelete,
}: CardProps) {
  // `editing` is null when not editing; otherwise it's the in-progress
  // draft. Avoids needing an effect to sync external name changes.
  const [editing, setEditing] = useState<string | null>(null);
  const isEditing = editing !== null;
  const draftName = editing ?? room.name;

  function commitRename() {
    if (editing === null) return;
    const trimmed = editing.trim();
    if (trimmed.length > 0 && trimmed !== room.name) {
      onRename(trimmed);
    }
    setEditing(null);
  }

  return (
    <li
      className={cn(
        "group relative flex items-stretch gap-3 p-2.5 rounded-2xl bg-white ring-1 transition-colors",
        isActive
          ? "ring-ink-900/80 bg-ink-50/60"
          : "ring-ink-200 hover:bg-ink-50"
      )}
    >
      <button
        type="button"
        onClick={onLoad}
        aria-label={`Load ${room.name}`}
        className="size-16 shrink-0 rounded-xl bg-ink-50 ring-1 ring-ink-100 overflow-hidden flex items-center justify-center"
      >
        <RoomPreview design={room.design} />
      </button>

      <div className="min-w-0 flex-1 flex flex-col justify-between py-0.5">
        <div className="min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={draftName}
                onChange={(e) => setEditing(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") setEditing(null);
                }}
                onBlur={commitRename}
                maxLength={80}
                className="flex-1 h-7 px-2 rounded-md bg-white ring-1 ring-ink-300 text-body-sm font-medium text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/40"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={onLoad}
              className="text-left text-sm font-semibold text-ink-900 truncate hover:underline decoration-ink-300 decoration-2 underline-offset-2 max-w-full"
            >
              {room.name}
              {isActive && (
                <span className="ml-1.5 align-middle inline-flex items-center px-1.5 rounded-full bg-ink-900 text-white text-label font-semibold">
                  current
                </span>
              )}
              {justSaved && (
                <span className="ml-1.5 align-middle inline-flex items-center px-1.5 rounded-full bg-emerald-100 text-emerald-700 text-label font-semibold">
                  saved
                </span>
              )}
            </button>
          )}
          <div className="mt-0.5 text-caption text-ink-500 truncate">
            {summarizeDesign(room.design)} ·{" "}
            <span title={new Date(room.updatedAt).toLocaleString()}>
              {formatRelative(room.updatedAt)}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-1 shrink-0 items-end">
        <button
          type="button"
          onClick={onLoad}
          aria-label={`Open ${room.name}`}
          title="Open"
          className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-ink-700 hover:bg-ink-100 active:bg-ink-200 transition-colors"
        >
          <Upload className="size-4 -rotate-90" />
        </button>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={() => {
              if (isEditing) {
                commitRename();
              } else {
                setEditing(room.name);
              }
            }}
            aria-label={isEditing ? "Confirm rename" : "Rename"}
            title={isEditing ? "Confirm" : "Rename"}
            className="h-7 w-7 inline-flex items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 active:bg-ink-200 transition-colors"
          >
            {isEditing ? <Check className="size-3.5" /> : <Pencil className="size-3.5" />}
          </button>
          {isEditing ? (
            <button
              type="button"
              onClick={() => setEditing(null)}
              aria-label="Cancel rename"
              title="Cancel"
              className="h-7 w-7 inline-flex items-center justify-center rounded-md text-ink-500 hover:bg-ink-100 active:bg-ink-200 transition-colors"
            >
              <X className="size-3.5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={onDelete}
              aria-label={`Delete ${room.name}`}
              title="Delete"
              className="h-7 w-7 inline-flex items-center justify-center rounded-md text-ink-500 hover:bg-rose-50 hover:text-rose-600 active:bg-rose-100 transition-colors"
            >
              <Trash2 className="size-3.5" />
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * Tiny SVG snapshot of a saved design: the room outline, furniture at
 * thumbnail LOD, and the doors, windows, and wall mounts. Auto-fits the
 * viewBox to the room's bounding box with a small padding.
 */
function RoomPreview({ design }: { design: Design }) {
  const { points } = design.room;
  if (points.length < 3) {
    return <div className="text-label text-ink-400">empty</div>;
  }
  const bb = boundingBox(points);
  const pad = Math.max(bb.width, bb.height) * 0.06 || 6;
  const vb = `${bb.minX - pad} ${bb.minY - pad} ${bb.width + pad * 2} ${
    bb.height + pad * 2
  }`;
  const polyPath = points.map((p) => `${p.x},${p.y}`).join(" ");
  // Stroke width should look constant regardless of room size.
  const stroke = Math.max(bb.width, bb.height) * 0.012 || 2;
  const openings = design.openings ?? [];
  const mounts = design.mounts ?? [];

  return (
    <svg
      data-testid="room-preview"
      viewBox={vb}
      className="size-full"
      preserveAspectRatio="xMidYMid meet"
    >
      <polygon
        points={polyPath}
        fill="white"
        stroke="rgb(60 60 70)"
        strokeWidth={stroke}
        strokeLinejoin="round"
      />
      {(design.room.interiorWalls ?? []).length > 0 &&
        interiorSegments(design.room).map((w) => (
          <line
            key={`iw-${w.wallId}-${w.segment}`}
            x1={w.a.x}
            y1={w.a.y}
            x2={w.b.x}
            y2={w.b.y}
            stroke="rgb(60 60 70)"
            strokeWidth={stroke}
            strokeLinecap="square"
          />
        ))}
      {design.furniture.map((f) => {
        // Same parts the canvas draws, at thumbnail LOD (no seams / textures).
        const plan = buildRenderPlan(resolveFurniture(f), 1, false);
        return (
          <g key={f.id} transform={`translate(${f.x} ${f.y}) rotate(${f.rotation})`} opacity={0.92}>
            <path d={toSvgPath(plan.footprintCmds)} fill={plan.baseFill} />
            {plan.parts.map((pd) => (
              <path key={pd.part.id} d={toSvgPath(pd.cmds)} fill={pd.paint.fill} opacity={pd.paint.opacity} />
            ))}
            <path d={toSvgPath(plan.footprintCmds)} fill="none" stroke="rgba(20,20,28,0.25)" strokeWidth={stroke * 0.4} />
          </g>
        );
      })}
      {mounts.map((m) => {
        const seg = mountOnWall(m, design.room);
        if (!seg) return null;
        return (
          <line
            key={m.id}
            x1={seg.start.x}
            y1={seg.start.y}
            x2={seg.end.x}
            y2={seg.end.y}
            stroke={seg.color}
            strokeWidth={stroke * 2.2}
            strokeLinecap="round"
          />
        );
      })}
      {openings.map((o) => {
        const seg = openingOnWall(o, design.room);
        if (!seg) return null;
        const gap = o.kind === "door" ? "#ffffff" : "#cfe2f3";
        const swing = {
          x: seg.start.x + seg.nx * o.width,
          y: seg.start.y + seg.ny * o.width,
        };
        return (
          <g key={o.id}>
            <line
              x1={seg.start.x}
              y1={seg.start.y}
              x2={seg.end.x}
              y2={seg.end.y}
              stroke={gap}
              strokeWidth={stroke * 1.8}
              strokeLinecap="butt"
            />
            {o.kind === "window" && (
              <line
                x1={seg.start.x}
                y1={seg.start.y}
                x2={seg.end.x}
                y2={seg.end.y}
                stroke="#5b9bd5"
                strokeWidth={Math.max(stroke * 0.35, 0.6)}
                strokeLinecap="butt"
              />
            )}
            {seg.slides &&
              slidingPanels(seg).map((p, i) => {
                const off = stroke * 0.45 * p.side;
                return (
                  <line
                    key={i}
                    data-testid="room-preview-slider"
                    x1={p.start.x + seg.nx * off}
                    y1={p.start.y + seg.ny * off}
                    x2={p.end.x + seg.nx * off}
                    y2={p.end.y + seg.ny * off}
                    stroke="#0c0c10"
                    strokeWidth={Math.max(stroke * 0.3, 0.5)}
                  />
                );
              })}
            {o.kind === "door" && !seg.swings && !seg.slides && (
              <>
                <line
                  data-testid="room-preview-cutout"
                  x1={seg.start.x}
                  y1={seg.start.y}
                  x2={seg.start.x + seg.nx * 6}
                  y2={seg.start.y + seg.ny * 6}
                  stroke="#0c0c10"
                  strokeWidth={Math.max(stroke * 0.25, 0.4)}
                />
                <line
                  x1={seg.end.x}
                  y1={seg.end.y}
                  x2={seg.end.x + seg.nx * 6}
                  y2={seg.end.y + seg.ny * 6}
                  stroke="#0c0c10"
                  strokeWidth={Math.max(stroke * 0.25, 0.4)}
                />
              </>
            )}
            {seg.swings && (
              <line
                data-testid="room-preview-door"
                x1={seg.start.x}
                y1={seg.start.y}
                x2={swing.x}
                y2={swing.y}
                stroke="#0c0c10"
                strokeWidth={Math.max(stroke * 0.25, 0.4)}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}

function summarizeDesign(design: Design): string {
  const area = polygonArea(design.room.points);
  const items = design.furniture.length;
  const piecesLabel = items === 1 ? "1 piece" : `${items} pieces`;
  return `${formatArea(area, design.unit)} · ${piecesLabel}`;
}
